import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { useScrollTo } from "@/hooks/useScrollTo";
import { NAV_LINKS, BUSINESS, mark } from "@/utils/constants";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const scrollTo = useScrollTo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ink/90 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
    >
      <Container className="flex items-center justify-between h-16 md:h-[76px]">
        <button data-testid="logo-home" onClick={() => go("home")} className="flex items-center gap-2.5 group">
          <img src={mark} alt="Deuce's mark" className="h-9 md:h-10 w-auto transition-transform group-hover:scale-105" />
          <span className="font-display text-2xl md:text-3xl text-white tracking-wide">DEUCE'S</span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-${l.to}`}
              onClick={() => go(l.to)}
              className="text-sm font-medium tracking-wide text-white/75 hover:text-weld-glow transition-colors uppercase"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a data-testid="nav-phone" href={BUSINESS.phoneHref} className="flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-weld-glow">
            <Phone size={16} /> {BUSINESS.phone}
          </a>
          <Button data-testid="nav-book" href={BUSINESS.bookUrl} target="_blank" rel="noopener noreferrer">
            Book Now
          </Button>
        </div>

        <button data-testid="mobile-menu-toggle" className="lg:hidden text-white" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </Container>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ink/98 backdrop-blur-md border-t border-white/5 px-5 py-6 flex flex-col gap-4">
          {NAV_LINKS.map((l) => (
            <button
              key={l.to}
              data-testid={`mobile-nav-${l.to}`}
              onClick={() => go(l.to)}
              className="text-left text-lg font-medium text-white/90 hover:text-weld-glow uppercase tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <a href={BUSINESS.phoneHref} className="flex items-center gap-2 text-white/90 font-semibold">
            <Phone size={18} /> {BUSINESS.phone}
          </a>
          <Button data-testid="mobile-nav-book" href={BUSINESS.bookUrl} target="_blank" rel="noopener noreferrer" size="lg" className="mt-2">
            Book Now
          </Button>
        </div>
      )}
    </header>
  );
};
